/**
 * Descarga por la capa NATIVA cuando el WebView no puede.
 *
 * POR QUÉ EXISTE (TKT-2286): en iOS el WKWebView corre bajo un esquema propio
 * (`binnacle://localhost`) y el CDN solo refleja el `Origin` cuando es http(s), así que no manda
 * `Access-Control-Allow-Origin`. Una `<img>` pinta igual (no requiere CORS) pero el XHR que baja
 * sus BYTES sí, y el WebView lo bloquea → axios devuelve *Network Error* → `responser.js` dispara
 * el toast "Error de conexión" con la red viva. `CapacitorHttp` pide desde NSURLSession, fuera del
 * WebView, así que no hay CORS que aplicar. Android usa `androidScheme: 'https'` (origen
 * `https://localhost`, sí permitido) y no necesita el fallback.
 */

/**
 * fetchNativeBase64 — el primitivo compartido: pide la URL por CapacitorHttp y devuelve el binario
 * en base64 con su mime ya normalizado, o `null` si no hay capa nativa / la respuesta no sirve.
 *
 * Se extrajo porque este mismo bloque (import dinámico, guard de plataforma, guard de status,
 * doble casing del `content-type`) vivía duplicado en `loadImageSafe.js` y en `fetchBlobSafe`
 * — que es justo el hallazgo de duplicación de SonarCloud. Ambos consumidores necesitan cosas
 * distintas del resultado (`loadImageSafe` arma un `data:` para el editor; `fetchBlobSafe` arma un
 * Blob), así que el primitivo devuelve la pieza cruda y cada uno la envuelve.
 *
 * @param {string} url
 * @param {{ httpGet?: Function, defaultMime?: string }} [options]
 *        httpGet: reemplaza a CapacitorHttp — SOLO para tests / inyección desde loadImageSafe.
 * @returns {Promise<{ base64: string, mime: string }|null>}
 */
export async function fetchNativeBase64 (url, { httpGet = null, defaultMime = 'application/octet-stream' } = {}) {
  let response = null
  if (httpGet) {
    response = await httpGet(url)
  } else {
    // Import dinámico para no romper entornos sin Capacitor (tests, web).
    const { Capacitor, CapacitorHttp } = await import('@capacitor/core')
    if (!Capacitor.isNativePlatform()) return null
    response = await CapacitorHttp.get({ url, responseType: 'blob' })
  }

  // CapacitorHttp NO rechaza por status: un 403/404 del CDN llega como respuesta normal con el
  // cuerpo de error (XML de S3, HTML) en `data`. Sin este guard, el `atob` de abajo revienta con
  // "The string to be decoded is not correctly encoded", que no dice nada del problema real.
  if (!(response && response.status >= 200 && response.status < 300 && response.data)) return null

  const rawMime = (response.headers && (response.headers['content-type'] || response.headers['Content-Type'])) || ''
  // El header puede traer parámetros (`image/png; charset=binary`). Se recortan porque el mime
  // termina en el `type` del Blob y, en LightboxPicture, en la EXTENSIÓN del archivo descargado
  // (`mime.split('/').pop()`) — sin recortar saldría un "foto.png; charset=binary".
  const mime = rawMime.split(';')[0].trim() || defaultMime

  return { base64: response.data, mime }
}

/**
 * fetchBlobSafe — descarga una URL como Blob: intenta primero por axios (el camino de siempre en
 * web y Android) y solo si falla reintenta por la capa nativa.
 *
 * @param {string}   url            URL HTTP a descargar
 * @param {Function} axiosInstance  this.$axios del componente que llama
 * @param {{ headers?: object }} [options]
 * @returns {Promise<{ blob: Blob, mime: string }>}
 * @throws si ambas rutas fallan — el componente llama notifyConnectionError() si lo desea
 */
/**
 * addFilesToZip — mete en un ZIP una lista de archivos bajados con `fetchBlobSafe` y devuelve
 * cuántos fallaron.
 *
 * El catch POR ARCHIVO es a propósito: una imagen rota no debe tumbar el ZIP completo. Pero el
 * conteo es necesario porque el primer intento de `fetchBlobSafe` viaja con
 * `suppressConnectionToast`, así que sin avisar al final el usuario se llevaría un ZIP incompleto
 * EN SILENCIO. El aviso (`notifyConnectionError`) lo hace el componente para no acoplar utils con
 * la capa de UI.
 *
 * Vive aquí porque `SubTable` y `SubTableByUsers` son gemelos y tenían este loop duplicado.
 *
 * @param {object}   zip            instancia de JSZip
 * @param {Array}    files          filas con `img_link` y `original_name`
 * @param {Function} axiosInstance  this.$axios del componente
 * @returns {Promise<{ failed: number }>}
 */
export async function addFilesToZip (zip, files, axiosInstance) {
  let failed = 0
  for (const file of files) {
    try {
      const { blob } = await fetchBlobSafe(file.img_link, axiosInstance)
      zip.file(file.original_name, blob, { binary: true })
    } catch (error) {
      failed++
      console.error('Error downloading the file:', error)
    }
  }
  return { failed }
}

export async function fetchBlobSafe (url, axiosInstance, { headers } = {}) {
  try {
    // suppressConnectionToast: si CapacitorHttp recupera el error, el toast sería ruido falso.
    const response = await axiosInstance({
      method: 'GET',
      url,
      responseType: 'blob',
      suppressConnectionToast: true,
      ...(headers ? { headers } : {})
    })
    return { blob: response.data, mime: response.data.type }
  } catch (error) {
    // `.catch(() => null)`: cualquier tropiezo del segundo intento (no hay capa nativa porque
    // estamos en web, CapacitorHttp rechaza, el CDN responde 403) se resuelve a null para poder
    // propagar el error ORIGINAL de axios. Si se lanzara uno genérico, el `console.error` del
    // componente perdería el motivo real (403 del CDN, timeout, CORS) y el fallo sería más difícil
    // de diagnosticar que antes del refactor.
    const native = await fetchNativeBase64(url).catch(() => null)
    if (!native) throw error
    // codePointAt en vez de charCodeAt (regla de Sonar): son equivalentes aquí porque `atob`
    // devuelve una binary string donde ningún carácter pasa de 0xFF, así que nunca hay pares
    // subrogados que los distingan.
    const bytes = Uint8Array.from(atob(native.base64), c => c.codePointAt(0))
    return { blob: new Blob([bytes], { type: native.mime }), mime: native.mime }
  }
}
